import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { avanzarEnTablero, calcularCasilleros } from '../../Tablero/tableroLogica';

const AdminTorneo = ({ session, esAdmin }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [torneo, setTorneo] = useState(null);
  const [participantes, setParticipantes] = useState([]);
  const [fechas, setFechas] = useState([]);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [puntosFecha, setPuntosFecha] = useState({});
  const [restriccionGol, setRestriccionGol] = useState({});
  const [cantRestricciones, setCantRestricciones] = useState({});
  const [presupuestoGastado, setPresupuestoGastado] = useState({});
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [msg, setMsg] = useState('');
  const [nombreFecha, setNombreFecha] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTodo(); }, [id]);

  const fetchTodo = async () => {
    setLoading(true);
    const [{ data: t }, { data: p }, { data: f }] = await Promise.all([
      supabase.from('torneos').select('*').eq('id', id).single(),
      supabase.from('torneo_participantes').select('*').eq('torneo_id', id),
      supabase.from('torneo_fechas').select('*').eq('torneo_id', id).order('numero_fecha'),
    ]);
    setTorneo(t);
    setParticipantes(p || []);
    setFechas(f || []);
    setLoading(false);
  };

  const crearFecha = async () => {
    const numero = fechas.length + 1;
    const { data, error } = await supabase.from('torneo_fechas').insert({
      torneo_id: id,
      numero_fecha: numero,
      nombre: nombreFecha || `Fecha ${numero}`,
      activa: true,
      cerrada: false,
    }).select().single();
    if (!error) {
      setFechas(prev => [...prev, data]);
      setFechaSeleccionada(data);
      setNombreFecha('');
      setMsg('✓ Fecha creada');
    }
  };

  const cargarPuntos = async () => {
    if (!fechaSeleccionada) { setMsg('Seleccioná una fecha primero'); return; }
    setGuardando(true); setMsg(''); setResultado(null);
    try {
      const ranking = participantes
        .map(p => ({ ...p, pts: parseInt(puntosFecha[p.user_id] || 0) }))
        .sort((a, b) => b.pts - a.pts);

      // Traer los drafts de todos los participantes para guardar snapshot
      const { data: drafts } = await supabase
        .from('drafts')
        .select('user_id, equipo, formacion, restricciones')
        .in('user_id', ranking.map(p => p.user_id));
      const draftMap = {};
      (drafts || []).forEach(d => { draftMap[d.user_id] = d; });

      const resumenMovimientos = [];

      for (let idx = 0; idx < ranking.length; idx++) {
        const p = ranking[idx];
        const posicion = idx + 1;
        const cant = parseInt(cantRestricciones[p.user_id] || 1);
        const tieneGol = restriccionGol[p.user_id] || false;
        const draft = draftMap[p.user_id] || {};

        await supabase.from('torneo_puntos').upsert({
          torneo_id: id,
          fecha_id: fechaSeleccionada.id,
          user_id: p.user_id,
          puntos: p.pts,
          presupuesto_gastado: parseInt(presupuestoGastado[p.user_id] || 0),
          equipo: draft.equipo || {},
          formacion: draft.formacion || null,
          restricciones: draft.restricciones || [],
        }, { onConflict: 'fecha_id,user_id' });

        const casilleros = calcularCasilleros({ posicion, cantidadRestricciones: cant, restriccionConGol: tieneGol });
        const mov = await avanzarEnTablero(id, p.user_id, casilleros);

        resumenMovimientos.push({
          nombre: p.nombre_usuario,
          pts: p.pts,
          posicion,
          casillerosMov: casilleros,
          casilleroAnterior: mov?.casilleroAnterior ?? 0,
          casilleroFinal: mov?.casilleroFinal ?? 0,
          vueltas: mov?.vueltasCompletadas ?? 0,
          comodines: mov?.comodinesGanados ?? [],
        });
      }

      await supabase.from('torneo_fechas').update({ cerrada: true, activa: false }).eq('id', fechaSeleccionada.id);

      // Actualizar palmarés del ganador (posicion === 1)
      const ganador = ranking[0];
      if (ganador && ganador.pts > 0) {
        const { data: perfilGanador } = await supabase.from('perfiles').select('fechas_ganadas, fechas_ganadas_tactico, fechas_ganadas_libre, racha_actual, mejor_racha, trofeos').eq('id', ganador.user_id).single();
        if (perfilGanador) {
          const nuevaRacha = (perfilGanador.racha_actual || 0) + 1;
          const trofeos = perfilGanador.trofeos || [];
          // Agregar trofeo si es la primera fecha ganada
          if ((perfilGanador.fechas_ganadas || 0) === 0) {
            trofeos.push({ tipo: 'fecha_ganada', descripcion: 'Primera fecha ganada', fecha: new Date().toISOString(), torneo_id: id });
          }
          await supabase.from('perfiles').update({
            fechas_ganadas: (perfilGanador.fechas_ganadas || 0) + 1,
            [`fechas_ganadas_${torneo.tipo}`]: ((perfilGanador[`fechas_ganadas_${torneo.tipo}`] || 0) + 1),
            racha_actual: nuevaRacha,
            mejor_racha: Math.max(perfilGanador.mejor_racha || 0, nuevaRacha),
            trofeos,
          }).eq('id', ganador.user_id);
        }
        // Resetear racha de los que no ganaron
        for (const p of ranking.slice(1)) {
          await supabase.from('perfiles').update({ racha_actual: 0 }).eq('id', p.user_id);
        }
      }

      setResultado(resumenMovimientos);
      setMsg('✓ Fecha cerrada');
      fetchTodo();
    } catch (e) {
      console.error(e);
      setMsg('Error: ' + e.message);
    } finally { setGuardando(false); }
  };

  const colorInput = { background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '6px', color: '#fff', padding: '6px 10px', width: '70px', textAlign: 'center', fontSize: '0.9rem' };

  if (loading) return <div style={{ padding: '40px', color: '#aaa' }}>Cargando...</div>;
  if (!torneo) return <div style={{ padding: '40px', color: '#ff4444' }}>Torneo no encontrado</div>;
  if (!esAdmin) return <div style={{ padding: '40px', color: '#ff4444' }}>Sin acceso</div>;

  return (
    <div style={{ padding: '24px 30px', color: 'white', maxWidth: '800px' }}>
      <button onClick={() => navigate(`/torneo/${id}`)}
        style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.85rem', padding: 0, marginBottom: '12px' }}>
        ← Volver al torneo
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem' }}>Admin — {torneo.nombre}</h1>
          <p style={{ color: '#64748b', margin: '2px 0 0', fontSize: '0.8rem' }}>Solo visible para el creador</p>
        </div>
        <span style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: '6px', padding: '4px 12px', fontSize: '0.8rem' }}>
          ★ Creador
        </span>
      </div>

      {/* VENTANA DE TRANSFERENCIAS */}
      <div style={{ background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ color: '#00f2ff', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 14px' }}>Ventana de Transferencias</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '0 0 4px' }}>APERTURA</p>
            <input type="datetime-local" id="apertura"
              style={{ ...colorInput, width: '200px', textAlign: 'left' }}
            />
          </div>
          <div>
            <p style={{ color: '#64748b', fontSize: '0.7rem', margin: '0 0 4px' }}>CIERRE</p>
            <input type="datetime-local" id="cierre"
              style={{ ...colorInput, width: '200px', textAlign: 'left' }}
            />
          </div>
          <button onClick={async () => {
            const apertura = document.getElementById('apertura').value;
            const cierre = document.getElementById('cierre').value;
            if (!apertura || !cierre) { alert('Completá ambas fechas'); return; }
            await supabase.from('ventana_transferencias').update({ activa: false }).eq('activa', true);
            await supabase.from('ventana_transferencias').insert({
              numero_fecha: fechas.length + 1,
              apertura, cierre, activa: true,
            });
            await supabase.from('drafts').update({
              confirmado: false,
              confirmado_en: null,
              ruleta_girada: false,
              efectos_comodines: [],
            });
            setMsg('✓ Ventana abierta. Confirmaciones, ruleta y efectos reseteados.');
          }} style={{ background: '#00f2ff', color: '#000', border: 'none', borderRadius: '6px', padding: '7px 18px', fontWeight: 'bold', cursor: 'pointer' }}>
            Abrir ventana
          </button>
        </div>
        <p style={{ color: '#334155', fontSize: '0.75rem', margin: '10px 0 0' }}>
          Al abrir una nueva ventana se resetean: confirmaciones, ruleta girada y efectos de comodines de todos los jugadores.
        </p>
      </div>

      {/* CREAR FECHA */}
      <div style={{ background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ color: '#00f2ff', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 14px' }}>Nueva Fecha</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input value={nombreFecha} onChange={e => setNombreFecha(e.target.value)}
            placeholder={`Fecha ${fechas.length + 1}`}
            style={{ ...colorInput, width: '200px', textAlign: 'left' }}
          />
          <button onClick={crearFecha} style={{ background: '#00f2ff', color: '#000', border: 'none', borderRadius: '6px', padding: '7px 18px', fontWeight: 'bold', cursor: 'pointer' }}>
            + Crear
          </button>
        </div>
      </div>

      {/* CARGAR PUNTOS */}
      <div style={{ background: '#0a0f19', border: '1px solid #1e293b', borderRadius: '12px', padding: '20px' }}>
        <h3 style={{ color: '#00f2ff', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 14px' }}>Cargar Puntos</h3>

        {/* Selector de fecha */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {fechas.map(f => (
            <button key={f.id} onClick={() => setFechaSeleccionada(f)}
              style={{
                padding: '6px 14px', borderRadius: '20px', border: '1px solid',
                borderColor: fechaSeleccionada?.id === f.id ? '#00f2ff' : '#1e293b',
                background: fechaSeleccionada?.id === f.id ? '#00f2ff22' : 'transparent',
                color: fechaSeleccionada?.id === f.id ? '#00f2ff' : '#64748b',
                cursor: f.cerrada ? 'not-allowed' : 'pointer', fontSize: '0.8rem',
                opacity: f.cerrada ? 0.5 : 1,
              }}>
              {f.nombre || `Fecha ${f.numero_fecha}`} {f.cerrada ? '✓' : ''}
            </button>
          ))}
          {fechas.length === 0 && <p style={{ color: '#334155', fontSize: '0.85rem', margin: 0 }}>Creá una fecha primero</p>}
        </div>

        {fechaSeleccionada && !fechaSeleccionada.cerrada && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 80px', gap: '8px', padding: '0 0 8px', borderBottom: '1px solid #1e293b', marginBottom: '8px' }}>
              <span style={{ color: '#64748b', fontSize: '0.72rem' }}>JUGADOR</span>
              <span style={{ color: '#64748b', fontSize: '0.72rem', textAlign: 'center' }}>PUNTOS</span>
              <span style={{ color: '#64748b', fontSize: '0.72rem', textAlign: 'center' }}>RESTRICCIONES</span>
              <span style={{ color: '#64748b', fontSize: '0.72rem', textAlign: 'center' }}>PRESUP. GASTADO</span>
              <span style={{ color: '#64748b', fontSize: '0.72rem', textAlign: 'center' }}>RESTR. GOL</span>
            </div>

            {participantes.map(p => (
              <div key={p.user_id} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px 80px', gap: '8px', padding: '8px 0', borderBottom: '1px solid #0f172a', alignItems: 'center' }}>
                <span style={{ color: '#e2e8f0', fontSize: '0.9rem' }}>{p.nombre_usuario}</span>
                <input type="number" min="0"
                  value={puntosFecha[p.user_id] || ''}
                  onChange={e => setPuntosFecha(prev => ({ ...prev, [p.user_id]: e.target.value }))}
                  placeholder="0" style={{ ...colorInput }}
                />
                <input type="number" min="1" max="4"
                  value={cantRestricciones[p.user_id] || ''}
                  onChange={e => setCantRestricciones(prev => ({ ...prev, [p.user_id]: e.target.value }))}
                  placeholder="1" style={{ ...colorInput }}
                />
                <input type="number" min="0"
                  value={presupuestoGastado[p.user_id] || ''}
                  onChange={e => setPresupuestoGastado(prev => ({ ...prev, [p.user_id]: e.target.value }))}
                  placeholder="0" style={{ ...colorInput, width: '90px' }}
                />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <input type="checkbox"
                    checked={restriccionGol[p.user_id] || false}
                    onChange={e => setRestriccionGol(prev => ({ ...prev, [p.user_id]: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#00f2ff' }}
                  />
                </div>
              </div>
            ))}

            <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <button onClick={cargarPuntos} disabled={guardando} style={{
                background: '#00f2ff', color: '#000', border: 'none', borderRadius: '8px',
                padding: '10px 24px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem',
              }}>
                {guardando ? 'Procesando...' : 'Cerrar Fecha y Mover Tablero'}
              </button>
              {msg && <span style={{ color: msg.startsWith('✓') ? '#22c55e' : '#ff4444', fontSize: '0.85rem' }}>{msg}</span>}
            </div>

            {/* RESUMEN DE MOVIMIENTOS */}
            {resultado && (
              <div style={{ marginTop: '20px', background: '#080d14', borderRadius: '10px', border: '1px solid #1e293b', overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid #1e293b' }}>
                  <span style={{ color: '#00f2ff', fontSize: '0.8rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>Resumen del movimiento</span>
                </div>
                {resultado.map((r, i) => (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '1fr 60px 120px 80px',
                    padding: '10px 16px', borderBottom: i < resultado.length-1 ? '1px solid #0f172a' : 'none',
                    alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: r.posicion === 1 ? '#f59e0b' : r.posicion === 2 ? '#94a3b8' : r.posicion === 3 ? '#b45309' : '#64748b', fontWeight: 'bold' }}>
                        #{r.posicion}
                      </span>
                      <span style={{ color: '#e2e8f0', fontSize: '0.9rem' }}>{r.nombre}</span>
                      {r.comodines.length > 0 && (
                        <span title={r.comodines.map(c => c.tipo).join(', ')}
                          style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b44', borderRadius: '10px', padding: '1px 7px', fontSize: '0.7rem' }}>
                          🃏 ×{r.comodines.length}
                        </span>
                      )}
                    </div>
                    <span style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'center' }}>{r.pts} pts</span>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '0.8rem' }}>{r.casilleroAnterior}</span>
                      <span style={{ color: '#334155', margin: '0 6px' }}>→</span>
                      <span style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '0.9rem' }}>{r.casilleroFinal}</span>
                      <span style={{ color: '#475569', fontSize: '0.75rem', marginLeft: '4px' }}>(+{r.casillerosMov})</span>
                    </div>
                    <span style={{ color: r.vueltas > 0 ? '#f59e0b' : '#334155', fontSize: '0.8rem', textAlign: 'right' }}>
                      {r.vueltas > 0 ? `🔄 ${r.vueltas} vuelta${r.vueltas > 1 ? 's' : ''}` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {fechaSeleccionada?.cerrada && (
          <p style={{ color: '#22c55e', fontSize: '0.85rem', margin: 0 }}>✓ Esta fecha ya fue cerrada</p>
        )}
      </div>
    </div>
  );
};

export default AdminTorneo;